'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'

// Types
import type { FeatureCardProps } from '@/types/components/molecules/solution'

// Components
const MenuItemIconAtom = dynamic(() => import('@/components/atoms/icon/menu-item'))
const ArrowRightIconAtom = dynamic(() => import('@/components/atoms/icon/bulk/arrow-right'))

// White card with a tinted icon, title and short description; lifts slightly on hover and can link to a page
const FeatureCardMolecule = (props: FeatureCardProps) => {
	// Props
	const { icon, title, description, href, linkText, compact } = props

	return (
		<Box
			{...(href && { component: Link, href })}
			sx={{
				height: 1,
				display: 'flex',
				flexDirection: 'column',
				p: compact ? 2.5 : { xs: 3, md: 4 },
				borderRadius: compact ? 4 : 5,
				bgcolor: 'common.white',
				color: 'text.primary',
				transition: 'transform .35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow .35s',
				'&:hover': { transform: compact ? 'translateY(-4px)' : 'translateY(-6px)', boxShadow: '0 24px 48px -20px rgba(23, 23, 23, 0.2)' },
				'&:hover .feature-icon': { bgcolor: 'primary.main', color: 'primary.contrastText' },
				'&:hover .feature-arrow': { translate: '4px 0' },
				'[dir=rtl] &:hover .feature-arrow': { translate: '-4px 0' }
			}}
		>
			<Box
				className="feature-icon"
				sx={{
					width: compact ? 40 : 52,
					height: compact ? 40 : 52,
					borderRadius: 3,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: 'primary.ultralight',
					color: 'primary.main',
					transition: 'background-color .35s, color .35s',
					'& .MuiSvgIcon-root': { fontSize: compact ? 20 : 26 }
				}}
			>
				<MenuItemIconAtom name={icon} />
			</Box>

			<Typography component="h3" sx={{ mt: compact ? 2 : 3, fontSize: compact ? 15.5 : 18, fontWeight: 700 }}>
				{title}
			</Typography>

			<Typography sx={{ mt: compact ? 0.5 : 1, fontSize: compact ? 13.5 : 16, lineHeight: compact ? 1.8 : 1.9, color: 'text.secondary', flexGrow: 1 }}>
				{description}
			</Typography>

			{href && linkText && (
				<Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.dark', fontSize: 15, fontWeight: 500 }}>
					{linkText}

					<Box
						className="feature-arrow"
						sx={{ display: 'flex', transition: 'translate .3s', '& .MuiSvgIcon-root': { fontSize: 20 }, '[dir=rtl] &': { transform: 'scaleX(-1)' } }}
					>
						<ArrowRightIconAtom />
					</Box>
				</Box>
			)}
		</Box>
	)
}

export default FeatureCardMolecule
